import React, { useState } from "react";
import {
  Card,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Space,
  Table,
  Alert,
  Modal,
  Empty,
  Popconfirm,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { saveOverride, deleteOverride } from "../../store/slice/pricing";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

const LOCALITIES = [
  { label: "Metro", value: "metro" },
  { label: "Urban", value: "urban" },
  { label: "Suburban", value: "suburban" },
  { label: "Rural", value: "rural" },
];

const TIERS = [
  { label: "High Demand", value: "high" },
  { label: "Standard", value: "standard" },
  { label: "Low Demand", value: "low" },
];

const PRICE_TYPES = [
  { label: "Joining Fee", value: "joiningFee" },
  { label: "Pro Monthly", value: "proMonthly" },
  { label: "Premium Monthly", value: "premiumMonthly" },
  { label: "Featured Weekly", value: "featuredWeekly" },
];

function OverrideManager() {
  const dispatch = useDispatch();
  const { overrides, baseFeesModel, demandMultipliers, isLoading } = useSelector(
    (state) => state.pricing
  );

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleAddOverride = async (values) => {
    setSubmitting(true);
    try {
      const overrideData = {
        id: editingId || `override_${Date.now()}`,
        ...values,
        appliedDate: new Date().toISOString(),
      };

      const resultAction = await dispatch(saveOverride(overrideData));
      if (saveOverride.fulfilled.match(resultAction)) {
        form.resetFields();
        setEditingId(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOverride = (override) => {
    setEditingId(override.id);
    form.setFieldsValue({
      locality: override.locality,
      tier: override.tier,
      priceType: override.priceType,
      price: override.price,
      reason: override.reason || "",
    });
  };

  const handleDeleteOverride = async (id) => {
    const resultAction = await dispatch(deleteOverride(id));
    if (deleteOverride.fulfilled.match(resultAction)) {
      form.resetFields();
      setEditingId(null);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingId(null);
  };

  const getCalculatedPrice = (locality, tier, priceType) => {
    if (priceType === "joiningFee") {
      return Math.round(baseFeesModel[locality] * demandMultipliers[tier]);
    }
    // For simplicity, using the same base fee for all price types
    return Math.round(baseFeesModel[locality] * demandMultipliers[tier]);
  };

  const columns = [
    {
      title: "Locality",
      dataIndex: "locality",
      key: "locality",
      width: 100,
      render: (text) => (
        <span className="capitalize font-medium">
          {LOCALITIES.find((l) => l.value === text)?.label || text}
        </span>
      ),
    },
    {
      title: "Tier",
      dataIndex: "tier",
      key: "tier",
      width: 120,
      render: (text) =>
        TIERS.find((t) => t.value === text)?.label || text,
    },
    {
      title: "Price Type",
      dataIndex: "priceType",
      key: "priceType",
      width: 130,
      render: (text) =>
        PRICE_TYPES.find((p) => p.value === text)?.label || text,
    },
    {
      title: "Override Price",
      dataIndex: "price",
      key: "price",
      width: 120,
      render: (price) => <span className="font-semibold">₹{price}</span>,
    },
    {
      title: "Calculated Price",
      key: "calculatedPrice",
      width: 140,
      render: (_, record) => {
        const calculated = getCalculatedPrice(
          record.locality,
          record.tier,
          record.priceType
        );
        const isDifferent = calculated !== record.price;
        return (
          <span className={isDifferent ? "text-red-600 font-medium" : "text-gray-600"}>
            ₹{calculated}
            {isDifferent && " (overridden)"}
          </span>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      render: (text) => <span className="text-gray-600 text-sm">{text || "—"}</span>,
    },
    {
      title: "Applied",
      dataIndex: "appliedDate",
      key: "appliedDate",
      width: 160,
      render: (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditOverride(record)}
            disabled={isLoading}
          />
          <Popconfirm
            title="Delete Override"
            description="Are you sure you want to delete this override?"
            onConfirm={() => handleDeleteOverride(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={isLoading}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Price Overrides"
      className="mb-6"
      extra={
        <span className="text-xs text-gray-600">
          Override base fee × multiplier for specific combos
        </span>
      }
    >
      <Space direction="vertical" className="w-full" size="large">
        {/* Add/Edit Override Form */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded">
          <h4 className="font-semibold mb-4">
            {editingId ? "Edit Override" : "Add Price Override"}
          </h4>

          <Alert
            type="warning"
            showIcon
            message="Override Behavior"
            description="An override will replace the calculated base fee × multiplier value for the selected combination."
            className="mb-4"
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddOverride}
            className="grid grid-cols-1 md:grid-cols-5 gap-4"
          >
            <Form.Item
              name="locality"
              label="Locality"
              rules={[{ required: true, message: "Select locality" }]}
            >
              <Select
                placeholder="Select locality"
                options={LOCALITIES}
                disabled={isLoading || submitting}
              />
            </Form.Item>

            <Form.Item
              name="tier"
              label="Demand Tier"
              rules={[{ required: true, message: "Select tier" }]}
            >
              <Select
                placeholder="Select tier"
                options={TIERS}
                disabled={isLoading || submitting}
              />
            </Form.Item>

            <Form.Item
              name="priceType"
              label="Price Type"
              rules={[{ required: true, message: "Select type" }]}
            >
              <Select
                placeholder="Select type"
                options={PRICE_TYPES}
                disabled={isLoading || submitting}
              />
            </Form.Item>

            <Form.Item
              name="price"
              label="Override Price (₹)"
              rules={[{ required: true, message: "Enter price" }]}
            >
              <InputNumber
                placeholder="₹"
                min={1}
                step={10}
                precision={0}
                disabled={isLoading || submitting}
              />
            </Form.Item>

            <Form.Item
              name="reason"
              label="Reason (Optional)"
              className="md:col-span-1"
            >
              <Input
                placeholder="Why this override?"
                disabled={isLoading || submitting}
                maxLength={100}
              />
            </Form.Item>

            <Form.Item className="md:col-span-5">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={isLoading}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {editingId ? "Update Override" : "Add Override"}
                </Button>
                {editingId && (
                  <Button onClick={handleCancel} disabled={isLoading}>
                    Cancel Edit
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </div>

        {/* Existing Overrides Table */}
        <div>
          <h4 className="font-semibold mb-3">Active Overrides ({overrides.length})</h4>

          {overrides.length === 0 ? (
            <Empty
              description="No price overrides configured"
              className="py-8"
            />
          ) : (
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={overrides}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `${total} overrides`,
                }}
                size="small"
                scroll={{ x: 900 }}
              />
            </div>
          )}
        </div>
      </Space>
    </Card>
  );
}

export default OverrideManager;
